<template>
  <div class="rd-screen">
    <report-tools :copy="hasRows" @copy="copyTable">
      <label class="rd-btn rd-btn--sm rd-toggle" :class="{ 'is-on': groupByEmployee }">
        <input v-model="groupByEmployee" type="checkbox" class="rd-visually-hidden" />
        <v-icon size="16" :color="groupByEmployee ? '#9a3412' : '#5f5852'">
          {{ groupByEmployee ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
        </v-icon>
        {{ $t("group_by_employee") }}
      </label>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!hasRows" icon="mdi-cash-off" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <div v-else-if="groupByEmployee" class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col">{{ $t("employee") }}</th>
            <th scope="col" class="is-right">{{ $t("count") }}</th>
            <th scope="col" class="is-right">{{ $t("amount") }}{{ currencySuffix }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in groupedRows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td class="is-right rd-num">{{ row.count }}</td>
            <td class="is-right rd-num">{{ money(row.amount) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ topups.length }}</td>
            <td class="is-right rd-num">{{ money(grandTotal) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col">{{ $t("date") }}</th>
            <th scope="col">{{ $t("employee") }}</th>
            <th scope="col">{{ $t("restaurant") }}</th>
            <th scope="col" class="is-right">{{ $t("amount") }}{{ currencySuffix }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in topups" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="rd-num">{{ row.dateText }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td :class="{ 'rd-muted': !row.restaurantId }">{{ row.restaurantText }}</td>
            <td class="is-right rd-num">{{ money(row.amount) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td></td>
            <td></td>
            <td class="is-right rd-num">{{ money(grandTotal) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabCreditTopups from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabCreditTopups.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Dopune kredita firme "više restorana" na računaru: svaka dopuna (datum, radnik, restoran, iznos) ili, uz
 * "Grupiši po radniku", broj dopuna i zbir po radniku. Period čita iz zajedničke trake. Nasleđuje učitavanje, štampu
 * i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiCreditTopups",
  extends: CompanyMultiRestaurantTabCreditTopups,
  mixins: [hasMultiFeed],
  components: { ReportState, ReportTools },
  computed: {
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.creditTopups.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Dopune za prikaz: datum i restoran već u tekstu (sve restorane ima dopuna bez restorana). */
    topups() {
      return this.creditTopups.map((item, index) => ({
        key: `${item.id || "t"}-${index}`,
        dateText: this.localeDate(item.date),
        name: this.toLatin(item.user_name || ""),
        restaurantId: item.restaurant_id,
        restaurantText: item.restaurant_id ? this.toLatin(item.restaurant_name) : this.$t("all_restaurants"),
        amount: item.total_amount,
      }));
    },
    groupedRows() {
      return this.groupedTopups.map((item, index) => ({
        key: `${item.user_name}-${index}`,
        name: this.toLatin(item.user_name || ""),
        count: item.topup_count,
        amount: item.total_amount,
      }));
    },
    kpis() {
      return [
        { key: "topups", label: this.$t("total_topups"), value: this.creditTopups.length, tone: "inverse" },
        { key: "amount", label: this.$t("amount"), value: this.money(this.grandTotal) },
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.groupedTopups.length },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = this.groupByEmployee
        ? [this.$t("employee"), this.$t("count"), this.$t("amount")]
        : [this.$t("date"), this.$t("employee"), this.$t("restaurant"), this.$t("amount")];
      const rows = this.groupByEmployee
        ? this.groupedRows.map((row) => [row.name, row.count, this.money(row.amount)])
        : this.topups.map((row) => [row.dateText, row.name, row.restaurantText, this.money(row.amount)]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
